import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";

function formatDate(dateStr) {
  return new Date(dateStr).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

const ArchivedPanelHeader = ({ count, loading, onClose }) => (
  <div className="flex items-center justify-between p-5 border-b border-slate-700">
    <div>
      <h2 className="text-xl font-bold text-white" data-testid="archived-panel-title">Archived Articles</h2>
      <p className="text-slate-400 text-sm mt-1">
        {loading ? "Loading..." : `${count} article(s) in archive`}
      </p>
    </div>
    <button
      data-testid="close-archived-panel"
      onClick={onClose}
      className="text-slate-400 hover:text-white transition-colors p-1"
    >
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
      </svg>
    </button>
  </div>
);

const ArchivedEmptyState = () => (
  <div className="text-center py-12" data-testid="no-archived-message">
    <svg className="w-16 h-16 mx-auto text-slate-600 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8" />
    </svg>
    <p className="text-slate-400 text-lg font-medium">No archived articles</p>
    <p className="text-slate-500 text-sm mt-2">
      Articles are automatically archived when you have more than 20 published.
      <br />The oldest articles beyond page 20 move here.
    </p>
  </div>
);

const ArchivedArticleItem = ({ article, restoring, onRestore }) => (
  <div
    data-testid={`archived-article-${article.id}`}
    className="flex items-start gap-4 bg-slate-800/60 rounded-xl p-4 border border-slate-700/50"
  >
    {(article.thumbnail_url || article.image_url) && (
      <img
        src={article.thumbnail_url || article.image_url}
        alt=""
        className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
      />
    )}
    <div className="flex-1 min-w-0">
      <h4 className="text-white font-semibold text-sm truncate">{article.title}</h4>
      <p className="text-slate-400 text-xs mt-1">
        By {article.author} &bull; {formatDate(article.created_at)}
      </p>
      <p className="text-slate-500 text-xs mt-1 line-clamp-2">{article.content}</p>
    </div>
    <Button
      data-testid={`restore-article-${article.id}`}
      onClick={() => onRestore(article.id)}
      disabled={restoring === article.id}
      size="sm"
      className="bg-yellow-600 hover:bg-yellow-500 text-white flex-shrink-0 px-4 py-2 min-h-[36px]"
    >
      {restoring === article.id ? "Restoring..." : "Restore"}
    </Button>
  </div>
);

const ArchivedPanelFooter = ({ count, loading, onRestoreAll, onClose }) => {
  if (loading) return null;

  if (count > 0) {
    return (
      <div className="p-5 border-t border-slate-700 flex gap-3">
        <Button
          data-testid="restore-all-archived"
          onClick={onRestoreAll}
          className="bg-yellow-600 hover:bg-yellow-500 text-white font-semibold flex-1"
        >
          Restore All ({count})
        </Button>
        <Button
          data-testid="close-archived-button"
          onClick={onClose}
          variant="outline"
          className="border-slate-600 text-slate-300 hover:bg-slate-800"
        >
          Close
        </Button>
      </div>
    );
  }

  return (
    <div className="p-5 border-t border-slate-700">
      <Button
        data-testid="close-archived-button"
        onClick={onClose}
        variant="outline"
        className="border-slate-600 text-slate-300 hover:bg-slate-800 w-full"
      >
        Close
      </Button>
    </div>
  );
};

const ArchivedArticlesPanel = ({ isOpen, onClose, fetchArchivedArticles, onUnarchiveOne, onUnarchiveAll, onRefresh }) => {
  const [archived, setArchived] = useState([]);
  const [loading, setLoading] = useState(false);
  const [restoring, setRestoring] = useState(null);

  const loadArchived = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchArchivedArticles();
      setArchived(data.articles || []);
    } catch {
      setArchived([]);
    } finally {
      setLoading(false);
    }
  }, [fetchArchivedArticles]);

  useEffect(() => {
    if (isOpen) loadArchived();
  }, [isOpen, loadArchived]);

  const handleRestoreOne = async (articleId) => {
    setRestoring(articleId);
    try {
      await onUnarchiveOne(articleId);
      setArchived((prev) => prev.filter((a) => a.id !== articleId));
      onRefresh();
    } catch {
      alert("Failed to restore article");
    } finally {
      setRestoring(null);
    }
  };

  const handleRestoreAll = async () => {
    if (archived.length === 0) {
      alert("No archived articles to restore.");
      return;
    }
    if (!window.confirm(`Restore all ${archived.length} archived article(s) back to the main feed?`)) return;
    try {
      const msg = await onUnarchiveAll();
      alert(msg);
      setArchived([]);
      onRefresh();
    } catch {
      alert("Failed to restore articles");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-start justify-center pt-12 sm:pt-20 px-4" onClick={onClose}>
      <div
        className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-2xl max-h-[80vh] flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <ArchivedPanelHeader count={archived.length} loading={loading} onClose={onClose} />

        <div className="flex-1 overflow-y-auto p-5">
          {loading && (
            <div className="text-center py-8">
              <p className="text-slate-400">Loading archived articles...</p>
            </div>
          )}

          {!loading && archived.length === 0 && <ArchivedEmptyState />}

          {!loading && archived.length > 0 && (
            <div className="space-y-3">
              {archived.map((article) => (
                <ArchivedArticleItem
                  key={article.id}
                  article={article}
                  restoring={restoring}
                  onRestore={handleRestoreOne}
                />
              ))}
            </div>
          )}
        </div>

        <ArchivedPanelFooter
          count={archived.length}
          loading={loading}
          onRestoreAll={handleRestoreAll}
          onClose={onClose}
        />
      </div>
    </div>
  );
};

export default ArchivedArticlesPanel;
